import React, { useContext, useEffect, useState } from "react";

import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  DeleteOutline,
  Edit,
  Refresh,
  CloudUpload,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import ForbiddenPage from "../../components/ForbiddenPage";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  statusChip: {
    textTransform: "capitalize",
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: 12,
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

const emptyForm = {
  title: "",
  description: "",
  content: "",
};

const statusColor = {
  pending: "default",
  indexing: "primary",
  ready: "primary",
  error: "secondary",
};

const InternalDocumentation = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchParam, setSearchParam] = useState("");

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/internal-documents", {
        params: searchParam ? { searchParam } : undefined,
      });
      const records = Array.isArray(data?.records)
        ? data.records
        : Array.isArray(data)
          ? data
          : [];
      setDocuments(records);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenCreate = () => {
    setSelected(null);
    setForm(emptyForm);
    setFile(null);
    setModalOpen(true);
  };

  const handleOpenEdit = async (doc) => {
    try {
      const { data } = await api.get(`/internal-documents/${doc.id}`);
      setSelected(data);
      setForm({
        title: data.title || "",
        description: data.description || "",
        content: data.content || "",
      });
      setFile(null);
      setModalOpen(true);
    } catch (err) {
      toastError(err);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelected(null);
    setForm(emptyForm);
    setFile(null);
  };

  const handleSave = async () => {
    if (!form.title.trim() && !file) {
      toast.error(i18n.t("internalDocumentation.toasts.titleRequired"));
      return;
    }
    if (!selected && !file && !form.content.trim()) {
      toast.error(i18n.t("internalDocumentation.toasts.contentRequired"));
      return;
    }

    setSaving(true);
    try {
      if (selected) {
        await api.put(`/internal-documents/${selected.id}`, {
          title: form.title,
          description: form.description,
          content: form.content,
        });
        toast.success(i18n.t("internalDocumentation.toasts.updated"));
      } else if (file) {
        const formData = new FormData();
        formData.append("file", file);
        if (form.title) formData.append("title", form.title);
        if (form.description) formData.append("description", form.description);
        await api.post("/internal-documents", formData);
        toast.success(i18n.t("internalDocumentation.toasts.created"));
      } else {
        await api.post("/internal-documents", {
          title: form.title,
          description: form.description,
          content: form.content,
          sourceType: "text",
        });
        toast.success(i18n.t("internalDocumentation.toasts.created"));
      }
      handleCloseModal();
      await loadDocuments();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await api.delete(`/internal-documents/${selected.id}`);
      toast.success(i18n.t("internalDocumentation.toasts.deleted"));
      setConfirmOpen(false);
      setSelected(null);
      await loadDocuments();
    } catch (err) {
      toastError(err);
    }
  };

  const handleReindex = async (doc) => {
    try {
      await api.post(`/internal-documents/${doc.id}/reindex`);
      toast.success(i18n.t("internalDocumentation.toasts.reindexed"));
      await loadDocuments();
    } catch (err) {
      toastError(err);
    }
  };

  if (user.profile === "user") {
    return (
      <MainContainer>
        <ForbiddenPage />
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selected
            ? `${i18n.t("internalDocumentation.confirmationModal.deleteTitle")} ${selected.title}?`
            : ""
        }
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setSelected(null);
        }}
        onConfirm={handleDelete}
      >
        {i18n.t("internalDocumentation.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle>
          {selected
            ? i18n.t("internalDocumentation.modal.edit")
            : i18n.t("internalDocumentation.modal.add")}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label={i18n.t("internalDocumentation.form.title")}
                variant="outlined"
                margin="dense"
                fullWidth
                value={form.title}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, title: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={i18n.t("internalDocumentation.form.description")}
                variant="outlined"
                margin="dense"
                fullWidth
                value={form.description}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, description: e.target.value }))
                }
              />
            </Grid>
            {!selected && (
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  {i18n.t("internalDocumentation.form.uploadHint")}
                </Typography>
                <div className={classes.fileRow}>
                  <Button
                    variant="outlined"
                    color="primary"
                    component="label"
                    startIcon={<CloudUpload />}
                  >
                    {i18n.t("internalDocumentation.form.upload")}
                    <input
                      type="file"
                      hidden
                      accept=".md,.txt,text/markdown,text/plain"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                    />
                  </Button>
                  {file && (
                    <Typography variant="body2">{file.name}</Typography>
                  )}
                </div>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label={i18n.t("internalDocumentation.form.content")}
                variant="outlined"
                margin="dense"
                fullWidth
                multiline
                rows={12}
                value={form.content}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, content: e.target.value }))
                }
                disabled={!!file && !selected}
                helperText={
                  file && !selected
                    ? i18n.t("internalDocumentation.form.contentFromFile")
                    : undefined
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary" variant="outlined">
            {i18n.t("internalDocumentation.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={saving}
          >
            {i18n.t("internalDocumentation.buttons.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <MainHeader>
        <Title>{i18n.t("internalDocumentation.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            size="small"
            variant="outlined"
            placeholder={i18n.t("internalDocumentation.searchPlaceholder")}
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") loadDocuments();
            }}
            style={{ marginRight: 8, minWidth: 200 }}
          />
          <Button
            variant="outlined"
            color="primary"
            onClick={loadDocuments}
            style={{ marginRight: 8 }}
          >
            {i18n.t("internalDocumentation.buttons.search")}
          </Button>
          <Button variant="contained" color="primary" onClick={handleOpenCreate}>
            {i18n.t("internalDocumentation.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("internalDocumentation.table.title")}</TableCell>
              <TableCell>
                {i18n.t("internalDocumentation.table.status")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("internalDocumentation.table.chunks")}
              </TableCell>
              <TableCell>
                {i18n.t("internalDocumentation.table.error")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("internalDocumentation.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((doc) => (
              <TableRow key={doc.id}>
                <TableCell>
                  <Typography variant="body2">{doc.title}</Typography>
                  {doc.description && (
                    <Typography variant="caption" color="textSecondary">
                      {doc.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={doc.status || "pending"}
                    color={statusColor[doc.status] || "default"}
                    className={classes.statusChip}
                  />
                </TableCell>
                <TableCell align="center">{doc.chunkCount ?? 0}</TableCell>
                <TableCell>
                  {doc.lastError ? (
                    <Typography
                      className={classes.errorText}
                      title={doc.lastError}
                    >
                      {doc.lastError}
                    </Typography>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleOpenEdit(doc)}>
                    <Edit />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleReindex(doc)}>
                    <Refresh />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelected(doc);
                      setConfirmOpen(true);
                    }}
                  >
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={5} />}
            {!loading && documents.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {i18n.t("internalDocumentation.empty")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default InternalDocumentation;
